// src/components/ValidateNavigator.tsx

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, UserAnswer, AssessmentResult, SkippedQuestion } from '../types';
import { questions } from '../data/questions';
import { getVisibleQuestions, getVisibleQuestionsAndSkipped, getNextQuestion, getProgressPercentage } from '../engine/branching';
import { calculateScore } from '../engine/scoring';
import QuestionCard from './QuestionCard';
import ProgressBar from './ProgressBar';
import ResultsDashboard from './ResultsDashboard';
import EmailCapture from './EmailCapture';
import { SkippedQuestionsReview } from './SkippedQuestionsReview';
import { LegalDisclaimer } from './LegalDisclaimer';
import NavigatorNav from './NavigatorNav';
import { InfoPanel } from './InfoPanel';

interface ValidateNavigatorProps {
  onBackToModes?: () => void;
}

const ValidateNavigator: React.FC<ValidateNavigatorProps> = ({ onBackToModes }) => {
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [answers, setAnswers] = useState<UserAnswer[]>([]);
  const [visibleQuestions, setVisibleQuestions] = useState<Question[]>([]);
  const [skippedQuestions, setSkippedQuestions] = useState<SkippedQuestion[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [showSkippedReview, setShowSkippedReview] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false); // Lifted state for tooltip

  // Update visible questions whenever answers change
  useEffect(() => {
    const { visible, skipped } = getVisibleQuestionsAndSkipped(questions, answers);
    setVisibleQuestions(visible);
    setSkippedQuestions(skipped);

    const answeredIds = answers.map(a => a.questionId);
    const next = getNextQuestion(visible, answeredIds);

    if (next) {
      setCurrentQuestion(next);
    } else {
      // All questions answered - show skipped review if there are skipped questions
      if (skipped.length > 0) {
        setShowSkippedReview(true);
      } else {
        // No skipped questions - go straight to results
        const finalResult = calculateScore(questions, answers);
        setResult(finalResult);
        setShowEmailCapture(true);
      }
    }
  }, [answers]);

  // Show disclaimer before starting
  if (!disclaimerAccepted) {
    return <LegalDisclaimer type="pre-assessment" onAccept={() => setDisclaimerAccepted(true)} />;
  }

  const handleContinueFromSkipped = () => {
    setShowSkippedReview(false);
    const finalResult = calculateScore(questions, answers);
    setResult(finalResult);
    setShowEmailCapture(true);
  };

  const handleAnswer = (questionId: string, value: string | string[]) => {
    // Update or add answer
    setAnswers(prev => {
      const existingIndex = prev.findIndex(a => a.questionId === questionId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { questionId, value };
        return updated;
      }
      return [...prev, { questionId, value }];
    });
  };

  const handleBack = () => {
    if (answers.length === 0) return;

    // Remove last answer
    setAnswers(prev => prev.slice(0, -1));
  };

  const handleEmailSubmit = (email: string) => {
    setUserEmail(email);
    setShowEmailCapture(false);
    // TODO: Send email to ConvertKit API
  };

  const handleRestart = () => {
    setAnswers([]);
    setResult(null);
    setShowEmailCapture(false);
    setUserEmail(null);
  };

  const progress = getProgressPercentage(answers.length, visibleQuestions.length);

  // Show skipped questions review
  if (showSkippedReview) {
    return (
      <SkippedQuestionsReview
        skippedQuestions={skippedQuestions}
        totalQuestionsAnswered={answers.length}
        onContinue={handleContinueFromSkipped}
      />
    );
  }

  if (showEmailCapture && result) {
    return (
      <EmailCapture
        mode="validate"
        result={result}
        implementationPlan={null}
        onSubmit={handleEmailSubmit}
        onSkip={() => setShowEmailCapture(false)}
      />
    );
  }

  if (result && userEmail) {
    return (
      <ResultsDashboard
        result={result}
        userEmail={userEmail}
        onRestart={handleRestart}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <NavigatorNav />
      <div className="py-8 px-4">
        <div className={`mx-auto transition-all duration-300 ${isInfoOpen ? 'max-w-7xl' : 'max-w-4xl'}`}>
          <div className="flex gap-6">
            {/* Main Content */}
            <div className={`transition-all duration-300 ${isInfoOpen ? 'flex-1' : 'w-full'}`}>
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-2">
                  HIPAA Compliance Navigator
                </h1>
                <p className="text-lg text-gray-600">
                  Comprehensive assessment covering all HIPAA requirements
                </p>
              </div>

              {/* Progress Bar */}
              <ProgressBar
                current={answers.length}
                total={visibleQuestions.length}
                percentage={progress}
              />

              {/* Question Card */}
              <AnimatePresence mode="wait">
                {currentQuestion && (
                  <motion.div
                    key={currentQuestion.id}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    <QuestionCard
                      question={currentQuestion}
                      onAnswer={handleAnswer}
                      onBack={handleBack}
                      canGoBack={answers.length > 0}
                      currentIndex={answers.length + 1}
                      totalQuestions={visibleQuestions.length}
                      isInfoOpen={isInfoOpen}
                      setIsInfoOpen={setIsInfoOpen}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer */}
              <div className="mt-8 text-center text-sm text-gray-500">
                <p>
                  🔒 Your responses are private and used only to generate your personalized roadmap
                </p>
                <p className="mt-2">
                  Based on official HIPAA regulations (45 CFR Part 164)
                </p>
              </div>
            </div>

            {/* Tooltip Panel - Side by side on desktop */}
            {isInfoOpen && currentQuestion && (
              <div className="hidden lg:block flex-shrink-0 w-96">
                <div className="sticky top-8">
                  <InfoPanel
                    question={currentQuestion}
                    isOpen={isInfoOpen}
                    onClose={() => setIsInfoOpen(false)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidateNavigator;
